import React, { useEffect, useRef, useState } from "react";
import { Clock, Play, Pause, Square, X, ChevronUp } from "lucide-react";

// LessonTimer — pinned floating widget for coaches to track time during a
// lesson or clinic. Persists across navigation and survives a refresh via
// localStorage. Single global timer (you're either timing one session or
// you're not). When idle the widget collapses to a tiny pill in the corner.
const STORAGE_KEY = "pbcoach.lessonTimer.v1";

function nowMs() { return Date.now(); }

function fmt(ms) {
  if (ms < 0) ms = 0;
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (typeof s.elapsed !== "number") return null;
    return s;
  } catch { return null; }
}

function saveState(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
}

export default function LessonTimer() {
  // state shape: { running: bool, elapsed: ms accumulated while paused, startedAt: ms when last resumed, label: string, expanded: bool }
  const [state, setState] = useState(() => loadState() || { running: false, elapsed: 0, startedAt: 0, label: "", expanded: false });
  const [, setTick] = useState(0);
  const intervalRef = useRef(null);

  // Tick once a second while running
  useEffect(() => {
    if (!state.running) return;
    intervalRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(intervalRef.current);
  }, [state.running]);

  // Persist on every meaningful change
  useEffect(() => { saveState(state); }, [state]);

  const liveMs = state.running ? state.elapsed + (nowMs() - state.startedAt) : state.elapsed;
  const isActive = state.running || state.elapsed > 0;

  const start = () => setState({ ...state, running: true, startedAt: nowMs(), expanded: true });
  const pause = () => setState({ ...state, running: false, elapsed: liveMs, startedAt: 0 });
  const stop = () => {
    if (state.running || state.elapsed > 0) {
      const ok = window.confirm(`End this timer? You spent ${fmt(liveMs)} on ${state.label || "this session"}.`);
      if (!ok) return;
    }
    setState({ running: false, elapsed: 0, startedAt: 0, label: "", expanded: false });
  };
  const setLabel = (label) => setState({ ...state, label });
  const toggleExpanded = () => setState({ ...state, expanded: !state.expanded });

  // Collapsed idle pill
  if (!isActive && !state.expanded) {
    return (
      <button
        onClick={() => setState({ ...state, expanded: true })}
        className="fixed bottom-4 right-4 z-40 bg-[#141414] border border-white/15 hover:border-[#38BDF8] hover:text-[#38BDF8] text-white px-3 py-2 text-[11px] uppercase tracking-widest font-bold inline-flex items-center gap-2 shadow-lg print:hidden"
        data-testid="lesson-timer-launcher"
        aria-label="Open lesson timer"
      >
        <Clock size={12} />
        Timer
      </button>
    );
  }

  // Expanded / running widget
  return (
    <div
      className={`fixed bottom-4 right-4 z-40 w-[min(20rem,calc(100vw-2rem))] bg-[#141414] border ${state.running ? "border-[#38BDF8]" : "border-white/15"} shadow-2xl print:hidden`}
      data-testid="lesson-timer-widget"
      role="region"
      aria-label="Lesson timer"
    >
      <div className="px-4 py-3 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold">
          <Clock size={12} className={state.running ? "text-[#38BDF8] animate-pulse" : "text-[#A3A3A3]"} />
          <span className={state.running ? "text-[#38BDF8]" : "text-[#A3A3A3]"}>
            {state.running ? "Live" : (state.elapsed > 0 ? "Paused" : "Ready")}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={toggleExpanded} className="text-white/60 hover:text-white" aria-label="Collapse" data-testid="lesson-timer-collapse">
            <ChevronUp size={14} className="rotate-180" />
          </button>
          <button onClick={stop} className="text-white/60 hover:text-rose-400" aria-label="Reset timer" data-testid="lesson-timer-reset">
            <X size={14} />
          </button>
        </div>
      </div>
      <div className="px-4 py-4 space-y-3">
        <input
          type="text"
          value={state.label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="What are you timing? (e.g. Fri 6pm Clinic)"
          className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#38BDF8] outline-none py-1 text-xs"
          data-testid="lesson-timer-label"
        />
        <div className="font-display text-5xl font-black tracking-tight tabular-nums text-center" data-testid="lesson-timer-display">
          {fmt(liveMs)}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {!state.running ? (
            <button
              onClick={start}
              className="btn-primary py-2 text-xs uppercase tracking-widest font-bold inline-flex items-center justify-center gap-2"
              data-testid="lesson-timer-start"
            >
              <Play size={12} />
              {state.elapsed > 0 ? "Resume" : "Start"}
            </button>
          ) : (
            <button
              onClick={pause}
              className="btn-secondary py-2 text-xs uppercase tracking-widest font-bold inline-flex items-center justify-center gap-2"
              data-testid="lesson-timer-pause"
            >
              <Pause size={12} /> Pause
            </button>
          )}
          <button
            onClick={stop}
            disabled={!isActive}
            className="btn-secondary py-2 text-xs uppercase tracking-widest font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="lesson-timer-stop"
          >
            <Square size={12} /> Reset
          </button>
        </div>
      </div>
    </div>
  );
}
